// GtHomeWatch — the pipeline funnel and the ranked attention list.
//
// This replaced six separate cards, each capped at a handful of rows. That
// works at two clients and fails at three hundred: you cannot scan six lists,
// and the first eight rows of each are not the eight things that matter.
//
// Instead every category is scored on one scale server-side (see attention.js)
// and merged into a single ordered list, so the top row is genuinely the most
// expensive thing being ignored. The categories become filters. Each row carries
// the reason it scored where it did, because a ranking nobody can interrogate is
// a ranking nobody trusts.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from '../../components/gtRoutes';

// Phases, not stage names: each role has its own stages, so the server groups
// every bucket by what it means and the funnel reads the same across all roles.
const FUNNEL_STAGES = [
  'new', 'in_progress', 'client_review', 'client_interested', 'client_interview', 'offer', 'placed',
];
const STAGE_LABEL = {
  new: 'New', in_progress: 'In progress', client_review: 'Client review',
  client_interested: 'Client interested', client_interview: 'Client interview',
  offer: 'Offer', placed: 'Placed', out: 'Out',
};

// Order matters: this is also the chip order, worst-first.
const TYPES = [
  { key: 'ownership_expiring', label: 'Ownership' },
  { key: 'awaiting_verdict',   label: 'No answer' },
  { key: 'empty_pipeline',     label: 'No candidates' },
  { key: 'unopened_share',     label: 'Not opened' },
  { key: 'quiet_client',       label: 'Going quiet' },
  { key: 'sweep_due',          label: 'Intelligence' },
];

// A raw score means nothing to a reader, so it is shown as a band.
function band(score) {
  if (score >= 70) return 'hot';
  if (score >= 40) return 'warm';
  return 'low';
}

export default function GtHomeWatch({ watch, authFetch }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [type, setType] = useState(null);
  const [limit, setLimit] = useState(8);

  const load = useCallback(async () => {
    try {
      const a = await authFetch('/api/home/attention?limit=' + limit + (type ? '&type=' + type : ''));
      setItems(a.items || []);
      setCounts(a.counts || {});
      setTotal(a.total || 0);
    } catch (e) { setItems([]); }
  }, [authFetch, type, limit]);

  useEffect(() => { load(); }, [load]);

  const funnelMap = {};
  ((watch && watch.funnel) || []).forEach(f => { funnelMap[f.stage] = f.n; });
  const funnelMax = Math.max(1, ...FUNNEL_STAGES.map(s => funnelMap[s] || 0));
  const funnelTotal = FUNNEL_STAGES.reduce((n, s) => n + (funnelMap[s] || 0), 0);
  const rejected = funnelMap.out || 0;

  function open(i) {
    if (!i.link_ref) return;
    if (i.link_type === 'candidate') navigate(paths.candidate(i.link_ref));
    else if (i.link_type === 'role') navigate(paths.role(i.link_ref));
    else if (i.link_type === 'client') navigate(paths.client(i.link_ref));
  }

  const shownCount = type ? (counts[type] || 0) : total;

  return (
    <div className="gthw">
      <div className="gthw-sec">
        <div className="gthw-head">
          <span className="gthw-title">Pipeline</span>
          <span className="gthw-count">{funnelTotal}</span>
        </div>
        <div className="gthw-hint">
          Across open roles{rejected ? ' \u00B7 ' + rejected + ' out' : ''}
        </div>
        {funnelTotal === 0 ? (
          <div className="gthw-empty">Nobody in any pipeline yet.</div>
        ) : (
          <div className="gthw-funnel">
            {FUNNEL_STAGES.map(st => {
              const n = funnelMap[st] || 0;
              return (
                <div key={st} className={'gthw-fstage' + (n ? '' : ' gthw-fstage--zero')}>
                  <span className="gthw-flabel">{STAGE_LABEL[st] || st}</span>
                  <span className="gthw-fbar">
                    <span className="gthw-ffill" style={{ width: n ? Math.max(6, (n / funnelMax) * 100) + '%' : '0%' }} />
                  </span>
                  <span className="gthw-fn">{n || ''}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="gthw-sec">
        <div className="gthw-head">
          <span className="gthw-title">Needs attention</span>
          <span className="gthw-count">{shownCount}</span>
        </div>
        <div className="gthw-hint">Ranked by what it costs to keep ignoring.</div>

        <div className="gthw-chips">
          <button type="button"
            className={'gthw-chip' + (type === null ? ' gthw-chip--on' : '')}
            onClick={() => { setType(null); setLimit(8); }}>
            All<span className="gthw-chip-n">{total}</span>
          </button>
          {TYPES.filter(t => counts[t.key]).map(t => (
            <button key={t.key} type="button"
              className={'gthw-chip' + (type === t.key ? ' gthw-chip--on' : '')}
              onClick={() => { setType(t.key); setLimit(8); }}>
              {t.label}<span className="gthw-chip-n">{counts[t.key]}</span>
            </button>
          ))}
        </div>

        {items === null ? (
          <div className="gthw-empty">Working it out</div>
        ) : items.length === 0 ? (
          <div className="gthw-empty">Nothing needs chasing. Every bundle has an answer and no clock is running down.</div>
        ) : (
          <>
            <ul className="gthw-list">
              {items.map((i, idx) => (
                <li key={i.type + idx}>
                  <button type="button" className={'gthw-arow gthw-arow--' + band(i.score)}
                    onClick={() => open(i)}>
                    <span className="gthw-arow-top">
                      <span className="gthw-row-main">{i.title}</span>
                      <span className="gthw-arow-type">{(TYPES.find(t => t.key === i.type) || {}).label || i.type}</span>
                    </span>
                    <span className="gthw-arow-reason">{i.reason}</span>
                    {i.subtitle ? <span className="gthw-row-meta">{i.subtitle}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
            {shownCount > items.length ? (
              <button type="button" className="gthw-more" onClick={() => setLimit(l => l + 20)}>
                Show more ({shownCount - items.length} left)
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
